import TrackingCookies from '../tracking_cookies.json';
import {
  DEFAULT_COOKIE_EXPIRATION_IN_MIN,
  LONG_TERM_COOKIE_EXPIRATION_IN_MIN,
  TRACKING_COOKIE_PREFIX,
} from './constants';
import * as Helper from './helpers/helper';
import * as windowHelper from './helpers/window_helper';
import { CookieUtils } from './types/cookies_utils';

const cookieMinutes = (minutes?: number) =>
  // minutes || DEFAULT_COOKIE_EXPIRATION_IN_MIN allows null passed to setCookie result in default
  minutes === 0 ? LONG_TERM_COOKIE_EXPIRATION_IN_MIN : minutes || DEFAULT_COOKIE_EXPIRATION_IN_MIN;

const cookieExpires = (minutesUntilExpiration?: number) => {
  const date = new Date();
  const minInMs = cookieMinutes(minutesUntilExpiration) * 60 * 1000;
  date.setTime(date.getTime() + minInMs);
  return `expires=${date.toUTCString()}`;
};

// Values can be compromised by malicious users to contain non-alphanumeric unicode characters. This
// causes kafka crashes. See alert-2162-kafka_consumers_are_not_starting
export function filterStringToAscii(value: string) {
  if (!value || value.length < 1) {
    return undefined;
  }

  return value.replace(/[\u{0080}-\u{FFFF}]/gu, '');
}

export function getCookieDomain() {
  const hostname = windowHelper.getWindowLocationHostname();
  const domainArr = hostname.split('.');
  const { length } = domainArr;

  // Make domain = top 2 domain components (ie. 'gusto.com') when cookies are set on Marketo (go.gusto.com) or blog
  if (length > 1) {
    const regex = /^gusto(-\w+)?\.com$/;
    const domain = `${domainArr[length - 2]}.${domainArr[length - 1]}`;

    if (regex.test(domain)) {
      return domain;
    }
  }

  return hostname;
}

// Returns undefined if not found
export function getCookie(name: string) {
  const cookies = document.cookie.split('; ');

  while (cookies.length) {
    const cookie = cookies.shift();
    const parts = (cookie ?? '').split(`${name}=`);

    if (parts.length > 1) {
      return filterStringToAscii(parts[1]);
    }
  }

  return undefined;
}

// Takes cookie name/key and cookie value and sets expiration to default time period set above
// If not specified, cookie expires in COOKIE_EXPIRATION_IN_DAYS (90)
// If minutesUntilExpiration = 0, then make cookie "permanent" (ie. expires 20 years from now, which aligns with
// cookies.permanent in Rails)
export function setCookie(
  cookieName: string,
  value?: string,
  minutesUntilExpiration?: number,
  length?: number,
  doc = document,
) {
  if (!value) {
    return;
  }
  const path = 'path=/';
  // Make domain = 'gusto.com' when cookies are set on Marketo (go.gusto.com) or blog
  const domain = `domain=${getCookieDomain()}`;

  doc.cookie = `${cookieName}=${[
    filterStringToAscii(value.substring(0, length || 255)),
    cookieExpires(minutesUntilExpiration),
    path,
    domain,
  ].join('; ')}`;
}

// Delete cookie
export function deleteCookie(cookieName: string) {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${getCookieDomain()}`;
}

// Return an object of cookies ({cookie_name: cookie_value}) that contains the prefix
export function getCookiesByPrefix(prefix: string) {
  const cookies = document.cookie.split('; ');
  return cookies
    .filter(c => c.startsWith(prefix))
    .map(c => c.split('='))
    .reduce((obj, [key, value]) => Object.assign(obj, { [key]: value }), {});
}

// Returns an object with cookies listed in the whitelist, and all cookies that starts with gst_*
export function getTrackingCookies(cookieUtils: CookieUtils) {
  const cookies: Record<any, any> = {};
  Object.entries(TrackingCookies).forEach(([key, value]) => {
    const cookieVal = cookieUtils.getCookie(key);
    if (cookieVal) {
      cookies[value] = cookieVal;
    }
  });
  // See if we have any cookies with the name gst_*
  return Helper.deepCopyMerge(cookies, cookieUtils.getCookiesByPrefix(TRACKING_COOKIE_PREFIX));
}

export const browserCookieUtils: CookieUtils = {
  deleteCookie: (name: string) => deleteCookie(name),
  getCookie: (name: string) => getCookie(name),
  getCookies: () => document.cookie.split('; '),
  getCookiesByPrefix: (prefix: string) => getCookiesByPrefix(prefix),
  setCookie: (
    name: string,
    value: string | undefined,
    minutesUntilExpiration?: number,
    length?: number,
  ) => setCookie(name, value, minutesUntilExpiration, length),
};
